import { useCallback, useEffect, useState } from "react";
import { Sparkles, Pencil, Info, Save } from "lucide-react";
import Sheet from "../../../components/sheet/Sheet";
import ClientService from "../../../services/client/ClientService";
import { showSuccess, notifyApiError } from "../../../components/alertas";
import logger from "../../../utils/logger";

const fmt = (n) => (Number(n) || 0).toLocaleString("es-MX");
const TONES = { relajado: "Relajado", formal: "Formal" };

/**
 * Tarjeta "Asistente con IA" de la pestaña Bot (add-on aiAssistant). Sin add-on explica qué
 * hace; con add-on muestra nombre, tono, información extra y el uso del mes, y deja editar
 * en un sheet (solo el dueño: el staff ve, no edita). El asistente y "Agendar por el chat"
 * son perillas independientes: si el agendado está apagado, contesta y manda el link.
 */
export default function BotAssistantCard({ clientData, refetchClientData, whatsappBookingEnabled, canEdit = true, side }) {
  const clientId = clientData?.id;
  const hasAddon = !!clientData?.subscription?.addons?.aiAssistant;
  const settings = clientData?.assistant || {};
  const enabled = settings.enabled !== false;
  const businessName = clientData?.business?.name || "tu negocio";

  const [usage, setUsage] = useState(null);
  const [editing, setEditing] = useState(false);

  const loadUsage = useCallback(async () => {
    if (!clientId || !hasAddon) return;
    try { setUsage(await ClientService.getAssistantUsage(clientId)); } catch (e) { logger.error("Error cargando uso del asistente:", e); }
  }, [clientId, hasAddon]);
  useEffect(() => { loadUsage(); }, [loadUsage]);

  const pill = !hasAddon
    ? <span className="cp-pill">No incluido</span>
    : usage?.capReached
      ? <span className="cp-pill cp-pill-warn">Al tope este mes</span>
      : enabled && usage?.platformReady !== false
        ? <span className="cp-pill cp-pill-ok">Activo</span>
        : <span className="cp-pill">Apagado</span>;

  const pct = usage?.cap > 0 ? Math.min(100, Math.round((usage.conversations / usage.cap) * 100)) : 0;

  return (
    <article className="cp-card cp-bot-card" data-tour="bot.assistant">
      <div className="cp-card-head">
        <h3 className="cp-card-title"><Sparkles size={18} /> Asistente con IA</h3>
        {pill}
      </div>

      {!hasAddon ? (
        <>
          <p className="cp-muted">
            Entiende lo que escriben tus clientes y contesta con tu ficha: precios, duraciones, horarios, ubicación y dudas.
            Los lleva a agendar en lenguaje natural, sin menús de números, y les explica los pasos con tus palabras.
          </p>
          <p className="cp-hint">Nunca confirma una cita por su cuenta: el sistema sigue revisando tu calendario. Escríbenos para activarlo.</p>
        </>
      ) : (
        <div className="cp-stack">
          <dl className="cp-kv">
            <dt>Se presenta como</dt>
            <dd>{settings.name?.trim() || <span className="cp-muted">el asistente de {businessName}</span>}</dd>
            <dt>Tono</dt>
            <dd>{TONES[settings.tone] || TONES.relajado}</dd>
            <dt>Información extra</dt>
            <dd className="cp-bot-clamp">{settings.extraInfo?.trim() || <span className="cp-muted">Sin información extra. Cuéntale lo que no está en tus servicios: estacionamiento, formas de pago, políticas.</span>}</dd>
          </dl>

          {usage && (
            <div className="cp-bot-usage" aria-label="Uso del mes">
              <div className="cp-bot-usage-bar"><span style={{ width: `${pct}%` }} /></div>
              <span className="cp-hint">
                {fmt(usage.conversations)} de {usage.cap > 0 ? fmt(usage.cap) : "∞"} conversaciones este mes
                {usage.capReached ? " · al llegar al tope el bot contesta con sus menús de siempre" : ""}
              </span>
            </div>
          )}

          {usage?.platformReady === false && (
            <div className="cp-note"><Info size={16} /><div>La IA todavía no está encendida en la plataforma. Mientras tanto el bot contesta con sus menús de siempre.</div></div>
          )}
          {!whatsappBookingEnabled && enabled && (
            <div className="cp-note"><Info size={16} /><div>El asistente contesta dudas y manda tu link para agendar. Enciende <b>Agendar por el chat</b> para que también agende por aquí.</div></div>
          )}

          {canEdit ? (
            <button type="button" className="cp-btn" onClick={() => setEditing(true)}><Pencil size={16} /> Editar asistente</button>
          ) : (
            <p className="cp-hint">El nombre, el tono y la información extra los configura el dueño del negocio.</p>
          )}
        </div>
      )}

      {hasAddon && (
        <AssistantSheet
          open={editing}
          side={side}
          clientId={clientId}
          settings={settings}
          businessName={businessName}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); refetchClientData?.(); loadUsage(); }}
        />
      )}
    </article>
  );
}

/** Sheet de edición: activo, nombre, tono e información extra. El tope de conversaciones lo fija TurnLink (superadmin). */
function AssistantSheet({ open, side, clientId, settings, businessName, onClose, onSaved }) {
  const [form, setForm] = useState({ enabled: true, name: "", tone: "relajado", extraInfo: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      enabled: settings.enabled !== false,
      name: settings.name || "",
      tone: settings.tone === "formal" ? "formal" : "relajado",
      extraInfo: settings.extraInfo || "",
    });
  }, [open, settings]);

  const save = async () => {
    setSaving(true);
    try {
      await ClientService.updateAssistantSettings(clientId, {
        enabled: form.enabled,
        name: form.name.trim(),
        tone: form.tone,
        extraInfo: form.extraInfo.trim(),
      });
      showSuccess("Asistente guardado", form.enabled ? "Ya contesta con esta configuración." : "El asistente queda apagado; el bot sigue con sus menús.");
      onSaved();
    } catch (e) {
      notifyApiError(e, "Guardar asistente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} side={side} title="Asistente con IA" subtitle="Cómo se presenta y qué sabe" className="cp-vars"
      footer={
        <div className="cp-sheet-actions">
          <button type="button" className="cp-btn" onClick={onClose}>Cancelar</button>
          <button type="button" className="cp-btn cp-btn-primary" onClick={save} disabled={saving}>{saving ? <span className="cp-spin" /> : <Save size={16} />} Guardar</button>
        </div>
      }
    >
      <div className="cp-form cp-bot-sheetform">
        <div className="cp-switch">
          <div><div className="cp-switch-label">Asistente activo</div><div className="cp-switch-hint">Apagado: el bot contesta con sus menús de siempre. Tu add-on sigue contratado.</div></div>
          <button type="button" role="switch" aria-checked={form.enabled} className="cp-toggle" onClick={() => setForm({ ...form, enabled: !form.enabled })} aria-label="Asistente activo" />
        </div>
        <label className="cp-field">
          <span>Nombre</span>
          <input type="text" className="cp-input" maxLength={60} placeholder={`el asistente de ${businessName}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <small>Cómo se presenta al saludar. Puedes dejarlo vacío.</small>
        </label>
        <label className="cp-field">
          <span>Tono</span>
          <select className="cp-input" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}>
            <option value="relajado">Relajado: cercano, como un buen recepcionista</option>
            <option value="formal">Formal: amable y correcto, sin modismos</option>
          </select>
          <small>Siempre tutea y habla en español de México; cambia el estilo.</small>
        </label>
        <label className="cp-field">
          <span>Información extra</span>
          <textarea className="cp-input" rows={5} maxLength={1500} placeholder="Ej: Hay estacionamiento gratis atrás. Aceptamos tarjeta y transferencia. Si llegas 10 min tarde se pierde el turno." value={form.extraInfo} onChange={(e) => setForm({ ...form, extraInfo: e.target.value })} />
          <small>Lo que no está en tus servicios ni en tu horario. El asistente solo afirma lo que está aquí o en tu ficha.</small>
        </label>
      </div>
    </Sheet>
  );
}
